import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import ApiLimitReachedBanner from "../../components/Banners/ApiLimitReachedBanner/ApiLimitReachedBanner.tsx";
import CustomerViewBanner from "../../components/Banners/CustomerViewBanner/CustomerViewBanner.tsx";
import UnderConstructionBanner from "../../components/Banners/UnderConstructionBanner/UnderConstructionBanner.tsx";
import PricingFlow from "../../components/Popups/Pricing/PricingFlow.tsx";
import CreateOrganizationPopup from "../../components/common/CreateOrganizationPopup/CreateOrganizationPopup.tsx";
import ExportImagePopup from "../../components/common/ExportImagePopup/ExportImagePopup.tsx";
import { ENV_VARS } from "../../config/env.ts";
import { getAwsRegistrationToken, getSelectedOrganization, setSelectedOrganization } from "../../helpers/localStorage";
import { useAppStore } from "../../hooks/useStores.tsx";
import { UserOrganization } from "../../models/organization.ts";
import migrationIframeStore from "../../pages/migration-iframe-store.tsx";

function getAllowedParentOrigin(): string | null {
	const url = ENV_VARS.VITE_NEW_PLATFORM_URL;
	if (!url || typeof url !== "string") return null;
	try {
		return new URL(url).origin;
	} catch {
		return null;
	}
}

const MigrationIframeLayout = () => {
	const navigate = useNavigate();
	const { uiStore } = useAppStore();
	const location = useLocation();

	useEffect(() => {
		const hubspotChatWidget = document.getElementById("hubspot-messages-iframe-container");
		if (hubspotChatWidget) {
			hubspotChatWidget.style.setProperty("display", "none", "important");
		}
	}, [location]);

	// Listen for navigation and set-current-org from parent (platform-frontend) via postMessage
	useEffect(() => {
		const allowedOrigin = getAllowedParentOrigin();
		const handler = (event: MessageEvent) => {
			if (allowedOrigin && event.origin !== allowedOrigin) return;
			console.log("Migration iframe event", JSON.stringify(event));
			if (event.data?.type === "MIGRATION_IFRAME_SET_CURRENT_ORG" && event.data?.selectedOrg) {
				const selectedOrg = event.data.selectedOrg as UserOrganization;
				const previousId = getSelectedOrganization()?.organization?.uid;
				const newId = selectedOrg?.organization?.uid;
				setSelectedOrganization(selectedOrg);
				if (previousId !== newId) {
					window.location.reload();
				}
			} else if (event.data?.type === "MIGRATION_IFRAME_SUBSCRIPTION_UPDATED") {
				window.location.reload();
			} else if (event.data?.type === "MIGRATION_IFRAME_JWT_UPDATED" && event.data?.jwtToken) {
				if (event.data?.jwtToken !== migrationIframeStore.jwtToken) {
					migrationIframeStore.setProperty("jwtToken", event.data?.jwtToken);
				}
			} else if (event.data?.type === "MIGRATION_IFRAME_NAVIGATE") {
				const path = event.data.path;
				if (typeof path === "string" && path) {
					// Merge parent query params with migration params (embed) so both are present in iframe URL
					const parentSearch = typeof event.data.search === "string" ? event.data.search : "";
					const migrationParams = new URLSearchParams(
						parentSearch.startsWith("?") ? parentSearch.slice(1) : parentSearch,
					);
					const search = migrationParams.toString();
					navigate(search ? { pathname: path, search } : path);
				}
			}
		};
		window.addEventListener("message", handler);
		return () => window.removeEventListener("message", handler);
	}, [navigate]);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
			<CustomerViewBanner />
			<ApiLimitReachedBanner />
			<UnderConstructionBanner />
			<CreateOrganizationPopup
				open={uiStore.CreateOrganizationDialog}
				onClose={() => uiStore.hideDialog("CreateOrganizationDialog")}
				keepAlive={getAwsRegistrationToken() !== null}
			/>
			<PricingFlow />
			{uiStore.ExportImagePopup && <ExportImagePopup open={uiStore.ExportImagePopup} />}
			<Outlet />
		</Box>
	);
};

export default observer(MigrationIframeLayout);
